/**
 *
 * @module commerceManager
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import SubmitOrderWithPaymentMethodRequest from './submitOrderWithPaymentMethodRequest';
import PaymentMethodType from './paymentMethodType';
import PaymentMethodResponse from './paymentMethodResponse';
import CardPaymentMethodResponse from './cardPaymentMethodResponse';

const Util = Services.Util;
const AccessTokenProvider = Token.AccessTokenProvider;
const CommerceManagerConfiguration = Services.Configuration.CommerceManagerConfiguration;
const CommerceClient = Services.Commerce.CommerceClient;
const LineItem = Services.Commerce.LineItem;
const OrderStatusResponse = Services.Commerce.OrderStatusResponse;
const PriceOrderRequest = Services.Commerce.PriceOrderRequest;
const SubmitOrderRequest = Services.Commerce.SubmitOrderRequest;
const CreatePaymentMethodRequest = Services.Commerce.CreatePaymentMethodRequest;
const ServicesSubmitOrderWithPaymentMethodRequest = Services.Commerce.SubmitOrderWithPaymentMethodRequest;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class CommerceManager
 *
 */
export default class CommerceManager {

    /**
     *
     * @param {SDK.Services.Configuration.CommerceManagerConfiguration} config
     * @param {SDK.Services.Commerce.CommerceClient} client
     * @param {SDK.Token.AccessTokenProvider} accessTokenProvider
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, client, accessTokenProvider, logger = Logging) {

        const errorMessage = {
            ctor: `${this.constructor.name}(config, client, accessTokenProvider, logger)`,
            get config() {
                return `${this.ctor} needs SDK.Services.Configuration.CommerceManagerConfiguration instance`;
            },
            get client() {
                return `${this.ctor} needs SDK.Services.Commerce.CommerceClient instance`;
            },
            get accessTokenProvider() {
                return `${this.ctor} needs SDK.Token.AccessTokenProvider instance`;
            }
        };

        instanceStrict(config, CommerceManagerConfiguration, errorMessage.config, InvalidArgumentException);
        instanceStrict(client, CommerceClient, errorMessage.client, InvalidArgumentException);
        instanceStrict(accessTokenProvider, AccessTokenProvider, errorMessage.accessTokenProvider, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.CommerceManagerConfiguration}
         *
         */
        this.config = config;

        /**
         *
         * @access private
         * @type {SDK.Services.Commerce.CommerceClient}
         *
         */
        this.client = client;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Number}
         *
         */
        this.counter = 0;

        /**
         *
         * @access private
         * @type {SDK.Token.AccessTokenProvider}
         *
         */
        this.accessTokenProvider = accessTokenProvider;

        this.logger.log(CommerceManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {String} namespace
     * @returns {Promise<SDK.Services.Commerce.GuidGeneratorResponse>}
     *
     */
    generateGuid(namespace) {

        const errorMessage = {
            method: `${this.constructor.name}.generateGuid(namespace)`,
            get namespace() {
                return `${this.method} namespace should be a String with length`;
            }
        };

        nonEmptyString(namespace, errorMessage.namespace, InvalidArgumentException);

        const { client } = this;

        return client.generateGuid(namespace);

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Commerce.PriceOrderRequest} request
     * @returns {Promise<SDK.Services.Commerce.PriceOrderResponse>}
     *
     */
    priceOrder(request) {

        const errorMessage = {
            method: `${this.constructor.name}.priceOrder(request)`,
            get request() {
                return `${this.method} needs SDK.Services.Commerce.PriceOrderRequest instance`;
            }
        };

        instanceStrict(request, PriceOrderRequest, errorMessage.request, InvalidArgumentException);

        const { client, accessToken } = this;

        return client.priceOrder(request, accessToken);

    }

    /**
     *
     * @access private
     * @param {String} zipCode
     * @returns {Promise<Array<SDK.Services.Commerce.ZipLocation>>}
     *
     */
    lookupByZipCode(zipCode) {

        const errorMessage = {
            method: `${this.constructor.name}.lookupByZipCode(zipCode)`,
            get zipCode() {
                return `${this.method} zipCode should be a String with length`;
            }
        };

        nonEmptyString(zipCode, errorMessage.zipCode, InvalidArgumentException);

        const { client } = this;

        return client.lookupByZipCode(zipCode);

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Commerce.SubmitOrderRequest} request
     * @returns {Promise<String>}
     * @note Uses the checkOrderStatusDelay value from the config to add a forced delay
     * after the request returns successfully. This is to allow the order to update on the service
     *
     */
    submitOrder(request) {

        const errorMessage = {
            method: `${this.constructor.name}.submitOrder(request)`,
            get request() {
                return `${this.method} needs SDK.Services.Commerce.SubmitOrderRequest instance`;
            }
        };

        instanceStrict(request, SubmitOrderRequest, errorMessage.request, InvalidArgumentException);

        const { client } = this;
        const orderId = Util.getSafe(() => request.passthroughData.guid);

        return client.submitOrder(request).then(() => {
            return Promise.resolve(orderId);
        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {SDK.Commerce.SubmitOrderWithPaymentMethodRequest} request
     * @returns {Promise<String>}
     *
     */
    submitOrderWithPaymentMethod(request) {

        const errorMessage = {
            ctor: `${this.constructor.name}.submitOrderWithPaymentMethod(request)`,
            get request() {
                return `${this.ctor} needs SDK.Commerce.SubmitOrderWithPaymentMethodRequest instance`;
            }
        };

        instanceStrict(request, SubmitOrderWithPaymentMethodRequest, errorMessage.request, InvalidArgumentException);

        const { client, accessToken } = this;
        const { cardSecurityCode, emailAddress, lineItems, locale } = request;
        const { orderCampaigns, paymentMethodId, redemptionRequest } = request;

        const appAccount = Util.getSafe(() => client.config.extras.appAccount);
        const servicesLineItems = lineItems.map((item) => new LineItem(item.sku, item.attributes));

        const options = {
            appAccount,
            cardSecurityCode,
            emailAddress,
            lineItems: servicesLineItems,
            locale,
            orderCampaigns,
            paymentMethodId,
            redemptionRequest
        };

        const clientRequest = new ServicesSubmitOrderWithPaymentMethodRequest(options);

        return client.submitOrderWithPaymentMethod(clientRequest, accessToken).then((paymentMethodResponse) => {

            const { guid } = paymentMethodResponse;

            return Promise.resolve(guid);

        });

    }

    /**
     *
     * @access private
     * @param {String} orderId
     * @returns {Promise<String>}
     * @desc 401 - Thrown when a user token is expired or invalid
     * @desc 422 - Thrown in the event of a billing failure
     * @desc 429 - Thrown after too many requests to the service have been made
     * @note return value is SDK.Services.Commerce.OrderStatusResponse enum
     *
     */
    getOrderStatus(orderId) {

        const { client, config, counter, accessToken } = this;

        const retryPolicy = Util.getSafe(() => config.extras.retryPolicy);
        const retryMaxAttempts = Util.getSafe(() => retryPolicy.retryMaxAttempts);
        const checkOrderStatusDelay = config.extras.checkOrderStatusDelay;
        const delayMultiplier = checkOrderStatusDelay < 4 ? 4 : checkOrderStatusDelay;
        const delayMilliseconds = delayMultiplier * 1000;

        const exceptions = [401, 422, 429];

        const delay = (time, value) => {
            return new Promise((resolve) => {
                setTimeout(resolve.bind(null, value), time);
            });
        };

        return delay(delayMilliseconds).then(() => {

            return client.getOrderStatus(orderId, counter, accessToken);

        }).then((response) => {

            if (Check.equal(response, OrderStatusResponse.Undetermined)) {
                this.counter++;
                return this.getOrderStatus(orderId);
            }

            // reset the counter on success case
            this.counter = 0;

            return Promise.resolve(response);

        }).catch((exception) => {

            const status = Util.getSafe(() => exception.status);

            if (counter > retryMaxAttempts) {

                // reset the counter after retry cycle
                this.counter = 0;

                return Promise.reject(exception);

            }

            if (!exceptions.includes(status)) {

                this.counter++;

                return this.getOrderStatus(orderId);

            }

            // reset the counter if no retry case is applicable
            this.counter = 0;

            return Promise.reject(exception);

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @param {SDK.Services.Commerce.CreatePaymentMethodRequest} request
     * @param {String} [region]
     * @returns {Promise<String>}
     *
     */
    createPaymentMethod(request, region) {

        const errorMessage = {
            method: `${this.constructor.name}.createPaymentMethod(request, region)`,
            get request() {
                return `${this.method} needs SDK.Services.Commerce.CreatePaymentMethodRequest instance`;
            },
            get region() {
                return `${this.method} region should be a String with length`;
            }
        };

        instanceStrict(request, CreatePaymentMethodRequest, errorMessage.request, InvalidArgumentException);

        if (Check.assigned(region)) {
            nonEmptyString(region, errorMessage.region, InvalidArgumentException);
        }

        const { client, accessToken } = this;

        const options = {
            request,
            region,
            accessToken
        };

        return client.createPaymentMethod(options).then((createPaymentMethodResponse) => {

            const paymentMethodId = createPaymentMethodResponse.paymentMethodId;

            return Promise.resolve(paymentMethodId);

        });

    }

    /**
     *
     * @access public
     * @since 3.5.0
     * @returns {Promise<SDK.Commerce.PaymentMethodResponse>}
     *
     */
    async getDefaultPaymentMethod() {

        const { client, accessToken } = this;

        const servicesPaymentMethodResponse = await client.getDefaultPaymentMethod(accessToken);

        return this.getPaymentMethodResponse(servicesPaymentMethodResponse);

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @param {String} paymentMethodId
     * @returns {Promise<SDK.Commerce.PaymentMethodResponse>}
     *
     */
    async getPaymentCard(paymentMethodId) {

        const errorMessage = {
            method: `${this.constructor.name}.getPaymentCard(paymentMethodId)`,
            get paymentMethodId() {
                return `${this.method} paymentMethodId should be a String with length`;
            }
        };

        nonEmptyString(paymentMethodId, errorMessage.paymentMethodId, InvalidArgumentException);

        const { client, accessToken } = this;

        const servicesPaymentMethodResponse = await client.getPaymentCard(paymentMethodId, accessToken);

        return this.getPaymentMethodResponse(servicesPaymentMethodResponse);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @returns {Promise<String>}
     *
     */
    getGuid() {

        const { client } = this;

        const namespace = Util.getSafe(() => client.config.extras.namespace);

        return this.generateGuid(namespace).then((response) => {

            const { token } = response;

            return Promise.resolve(token);

        });

    }

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.accessTokenProvider.getAccessToken();

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Commerce.PaymentMethodResponse} servicesPaymentMethodResponse
     * @returns {SDK.Commerce.PaymentMethodResponse}
     *
     */
    getPaymentMethodResponse(servicesPaymentMethodResponse) {

        if (PaymentMethodType[servicesPaymentMethodResponse.type] === PaymentMethodType.PaymentCard) {

            return new CardPaymentMethodResponse(servicesPaymentMethodResponse);

        } else {

            return new PaymentMethodResponse(servicesPaymentMethodResponse);

        }

    }

}



// WEBPACK FOOTER //
// ./src/commerce/commerceManager.js